import React, { useContext, useEffect, useState } from 'react';
import { useFormikContext } from 'formik';
import { get } from 'lodash';

import { DroneContext, FormContext } from 'contexts';
import { Button, Loader } from 'components/common';
import {
  AuSelect,
  AuTextInput,
  AuCallout,
  AuControlInput,
  AuFormItemWrapper,
  AuFormGroup
} from 'components/DTA';
import {
  ControlInputGroup,
  StepContainer,
  FormNavigation,
  ApplicationName
} from 'components/CASAForm';
import { DroneEditStepProps, DroneData, DroneRegistrationFormData } from 'types/forms/droneRegistrationTypes';
import { IFormAlertType } from 'types/forms/formTypes';
import { DRONE_CONSTANTS, FORM_ERROR_MESSAGES } from 'internalConstants';
import { IDroneReferenceData } from 'services/droneService';
import { useDroneService } from 'services';
import determineFee from '../utilities/DroneDetermineFee';

const getDroneDefaults = (id: number, isFeatureRecDrone: boolean): DroneData => {
  return {
    id: id,
    manufacturerCode: '',
    manufacturer: '',
    modelCode: '',
    model: '',
    categoryCode: '',
    category: '',
    weightClassCode: '',
    weightClass: '',
    serialNumber: '',
    noSerialNumber: false,
    intendedUse: isFeatureRecDrone ? '' : 'commercial',
    registeredOverseas: 'no',
    isDraft: true
  };
};

const DroneEditStep: React.FC<DroneEditStepProps> = ({ droneIndex = undefined, applicationName, parent = 'registration' }) => {
  const { setFormAlert, openStepById, setOnChangeValidation, setOnBlurValidation, } = useContext(FormContext);
  const {
    referenceData,
    rateCards: rateCards,
    isFeatureRecDrone,
    featureDroneSerialNumberValidationIsActive,
    checkIfOtherManufacturer,
    checkIfOtherModel,
    checkIfHomeBuilt,
    checkIfAirship,
    setCheckSerialNumberPrefix
  } = useContext(DroneContext);
  const { values, errors, setFieldValue, validateForm, setFieldError } = useFormikContext<DroneRegistrationFormData>();

  const [currentDroneIndex, setCurrentDroneIndex] = useState<number | null>(null);

  const currentDroneValues = currentDroneIndex !== null ? values.drones[currentDroneIndex] : getDroneDefaults(1, isFeatureRecDrone);

  const currentModel = currentDroneValues?.modelCode && referenceData && referenceData.value && referenceData?.value['DRONE_MODEL'].find(model => model.casa_code === currentDroneValues.modelCode);

  const [previousCategoryCode, setPreviousCategoryCode] = useState<string | null | undefined>(null);
  const [weightClassList, setWeightClassList] = useState<IDroneReferenceData[]>([]);
  const [savedDroneData, setSavedDroneData] = useState<DroneData>();
  const [showLoader, setShowLoader] = useState<boolean>(false);
  const droneService = useDroneService();

  useEffect(() => {
    // Set the current drone index into state based on props
    if (droneIndex === undefined) {
      if (values.drones.length > 0) {
        // draft application
        // loop through array of drones to find draft drone, if no draft drone is found then proceed to Drone List step
        const index = getDraftDroneIndex();
        index !== undefined ? setCurrentDroneIndex(index) : openStepById('DroneList');
      }
      else {
        // new application
        setCurrentDroneIndex(0);
      }
    }
    else {
      // any action from the Drone List step
      // if drone already exists then set draft status
      if (values.drones[droneIndex]) {
        setSavedDroneData(values.drones[droneIndex]);
        setFieldValue(`drones[${droneIndex}].isDraft`, true); // set draft status
        setCurrentDroneIndex(droneIndex);
      }
      else {
        setCurrentDroneIndex(droneIndex);
      }
    }

    // Clear form alert on load
    setFormAlert(null);
  }, []);

  useEffect(() => {
    // Set default values if one doesn't exist
    if (currentDroneIndex !== null && !values.drones[currentDroneIndex]) {
      const id = values.drones.length > 0 ? Math.max(...values.drones.map(drone => drone.id)) + 1 : 1;
      setFieldValue(`drones[${currentDroneIndex}]`, getDroneDefaults(id, isFeatureRecDrone));
    }
  }, [currentDroneIndex]);

  useEffect(() => {
    if (!currentDroneValues || !referenceData || !referenceData.value) {
      return;
    }
    setWeightClassList(referenceData?.value['DRONE_WEIGHT_CLASS'] ?? []);
    // Reset model value when manufacturer changes
    if (currentDroneValues.modelCode && (!currentModel || currentModel['parent.casa_code'] !== currentDroneValues.manufacturerCode)) {
      setFieldValue(`drones[${currentDroneIndex}].modelCode`, '');
      setFieldValue(`drones[${currentDroneIndex}].manufacturer`, '');
      setFieldValue(`drones[${currentDroneIndex}].model`, '');
    }
  }, [currentDroneValues?.manufacturerCode]);

  // on change of model
  useEffect(() => {
    if (!currentDroneValues || !referenceData || !referenceData.value) {
      return;
    }
    // set weight class code based off the model
    const weightClassCode = currentModel != null && currentModel != '' ? currentModel?.droneWeightClassCode : '';
    if (weightClassCode) {
      setFieldValue(`drones[${currentDroneIndex}].weightClassCode`, weightClassCode);
    }
    const weightClass = currentModel != null && currentModel != '' ? currentModel?.droneWeightClass : '';
    if (weightClass) {
      setFieldValue(`drones[${currentDroneIndex}].weightClass`, weightClass);
    }
    // If the model selected has a serial number prefix then set noSerialNumber to false
    // as a serial number must be provided by the user.
    if (currentModel && currentModel.casa_serialnumberprefix) {
      setFieldValue(`drones[${currentDroneIndex}].noSerialNumber`, false);
    }
  }, [currentDroneValues?.modelCode]);

  useEffect(() => {
    if (checkIfAirship(currentDroneValues?.categoryCode)) {
      setFieldValue(`drones[${currentDroneIndex}].weightClassCode`, DRONE_CONSTANTS.AIRSHIP_CATEGORY_WEIGHT_CLASS);
    }
    else {
      if (checkIfAirship(previousCategoryCode)) {
        setFieldValue(`drones[${currentDroneIndex}].weightClassCode`, '');
      }
    }
    setPreviousCategoryCode(currentDroneValues?.categoryCode);
  }, [currentDroneValues?.categoryCode]);

  // on change of serial number checkbox
  useEffect(() => {
    // if no serial number then clear any previously entered serial number value
    if (currentDroneValues?.noSerialNumber) {
      setFieldValue(`drones[${currentDroneIndex}].serialNumber`, '');
    }
  }, [currentDroneValues?.noSerialNumber]);

  const getDraftDroneIndex = (): number | undefined => {
    for (let index = 0; index < values.drones.length; index++) {
      if (values.drones[index].isDraft) {
        return index;
      }
    }
    return undefined;
  };

  const saveDrone = async () => {
    setFormAlert(null);
    window.scrollTo(0, 0);
    // Run step validation
    const validationErrors = await validateForm();
    const droneInvalid = validationErrors.drones ? validationErrors.drones[currentDroneIndex ? currentDroneIndex : 0] ? true : false : false;

    // Proceed to DroneList if there aren't any errors
    if (!droneInvalid) {
      const drone = values.drones[currentDroneIndex ? currentDroneIndex : 0];
      const currentManufacturer = drone?.manufacturerCode ? referenceData?.value['DRONE_MANUFACTURER'].find(manufacturer => manufacturer.casa_code === drone.manufacturerCode) : null;
      const currentModel = drone.modelCode ? referenceData?.value['DRONE_MODEL'].find(model => model.casa_code === drone.modelCode) : null;

      // if DJI drone validate the serial number against the DJI API
      let serialNumberValid = true;
      if (featureDroneSerialNumberValidationIsActive && drone.manufacturerCode === DRONE_CONSTANTS.DJI_MANUFACTURER_CODE && drone.serialNumber) {
        // for testing DJI Inspire 2, serial number 095XDC2002023Q
        setShowLoader(true);
        const serialNumberToCheck = drone.serialNumber;
        const response = await droneService.searchDroneSerialNumber({
          serialNumber: serialNumberToCheck
        });
        setShowLoader(false);
        serialNumberValid = response.wasSuccessful && response.responseData.serialNumber === serialNumberToCheck && response.responseData.valid;
        if (response.responseData.apiFailure) {
          // api failed validate the serial number prefix and set serial number invalid if prefix does not match
          //setCheckSerialNumberPrefix(true);
          const validationErrors = await validateForm();
          //setCheckSerialNumberPrefix(false);
          serialNumberValid = !(validationErrors.drones ? validationErrors.drones[currentDroneIndex ? currentDroneIndex : 0] ? true : false : false);
        }
      }

      if (serialNumberValid) {
        setFieldValue(`drones[${currentDroneIndex}].fee`, determineFee(rateCards, currentModel?.droneWeightClassCode || drone.weightClassCode, drone.registeredOverseas === 'yes', drone.intendedUse === 'commercial'));
        //const currentCategory = drone?.categoryCode ? referenceData?.value['DRONE_CATEGORY'].find(category => category.casa_code === drone.categoryCode) : null;
        if (!drone.manufacturer) {
          setFieldValue(`drones[${currentDroneIndex}].manufacturer`, currentManufacturer?.casa_name);
        }
        if (!drone.model) {
          setFieldValue(`drones[${currentDroneIndex}].model`, currentModel?.casa_name);
        }
        // if (!drone.category) {
        //   setFieldValue(`drones[${currentDroneIndex}].category`, currentModel?.casa_name);
        // }
        setFieldValue(`drones[${currentDroneIndex}].isDraft`, false);
        setFieldValue(`drones[${currentDroneIndex}].modelUpdateRequired`, false);
        setFieldValue(`drones[${currentDroneIndex}].serialNumberUpdateRequired`, false);
        setFieldValue(`drones[${currentDroneIndex}].updatesSaved`, parent === 'registration' ? false : true);
        if (parent === 'registration') {
          const alert: IFormAlertType = {
            as: 'success',
            title: 'Drone added to application'
          };
          setFormAlert(alert);
        }
        if (parent === 'renewal') {
          const alert: IFormAlertType = {
            as: 'success',
            title: 'Updates saved',
            message: 'Drone details updated successfully.'
          };
          setFormAlert(alert);
        }
        setOnChangeValidation(false);
        setOnBlurValidation(false);
        parent === 'registration' ? openStepById('DroneList') : openStepById('DroneRenewalSummary');
      }
      else {
        // serial number did not pass validation on DJI API
        // set form alert and mark field as in error
        // do not turn on onChange or OnBlur validation as this error can only be cleared by subsequent call to API
        setFieldError(`drones[${currentDroneIndex}].serialNumber`, FORM_ERROR_MESSAGES.PATTERN_ERROR);
        showProblemWithFormAlert();
        setOnChangeValidation(false);
        setOnBlurValidation(false);
      }
    }
    else {
      showProblemWithFormAlert();
      setOnChangeValidation(true);
      setOnBlurValidation(true);
    }
  };

  const showProblemWithFormAlert = () => {
    const alert: IFormAlertType = {
      as: 'error',
      title: 'There was a problem with the form',
      message: 'Please check the form below and try again'
    };
    setFormAlert(alert);
  };

  const isLightWeightRecreationalDrone = (): boolean => {
    if (currentDroneValues.intendedUse === 'recreational') {
      if (currentModel && currentModel.casa_droneweight > 0 && currentModel.casa_droneweight < 250) {
        return true;
      }
      const gramsMax = referenceData?.value['DRONE_WEIGHT_CLASS'].find(m => m.casa_code === currentDroneValues.weightClassCode)?.casa_gramsmax;
      if (gramsMax && gramsMax <= 250) {
        return true;
      }
    }
    return false;
  };

  const getSerialNumberPrefixCallout = (): React.ReactNode => {
    const droneManufacturerName = currentDroneIndex !== null ? referenceData?.value['DRONE_MANUFACTURER'].find(m => m.casa_code === values.drones[currentDroneIndex].manufacturerCode)?.casa_name : '';
    const droneModelName = currentModel ? currentModel.casa_name : '';
    const serialNumberRange = currentModel && currentModel.casa_snlengthrange ? currentModel.casa_snlengthrange : null;
    const serialNumberLengthString = serialNumberRange ? ` is ${serialNumberRange} characters and ` : ' ';
    const dronePrefixes = currentModel && currentModel.casa_serialnumberprefix ? currentModel.casa_serialnumberprefix.split(',') : [];
    const lastDronePrefix = dronePrefixes.pop();
    const dronePrefixString = dronePrefixes.length > 0 ? `${dronePrefixes.join(', ')} or ${lastDronePrefix}` : lastDronePrefix;
    return <AuCallout style={{ maxWidth: '34.5em' }}>
      <strong>Your {droneManufacturerName} {droneModelName} serial number{serialNumberLengthString}will start with:<br /><span style={{ fontSize: '1.5rem', fontFamily: 'monaco' }}>{dronePrefixString}</span></strong>
    </AuCallout>;
  };

  if (currentDroneIndex == null || currentDroneValues == null) {
    return null;
  }

  if (!referenceData || !referenceData.value || showLoader) {
    return <Loader message='Working. Please wait.....' />;
  }

  return (
    <>
      <ApplicationName name={applicationName} />
      <StepContainer
        title='Drone details'
        customHandleBack={(e: React.MouseEvent<HTMLElement>) => {
          e.preventDefault();
          if (savedDroneData) {
            values.drones[currentDroneIndex] = savedDroneData;
            if (parent === 'renewal') {
              openStepById('DroneDetails', { droneIndex });
            }
            else {
              openStepById('DroneList');
            }
          }
          else {
            const deletedDrone = values.drones[currentDroneIndex];
            values.drones = values.drones.filter(drone => drone != deletedDrone);
            if (values.drones.length > 0) {
              openStepById('DroneList');
            }
            else {
              openStepById('BeginRegistration');
            }
          }
        }}
      >
        <div className='no-gutters row'>
          <div className='float-none col-md-12 col-lg-12'>
            <AuFormGroup status={get(errors, `drones[${currentDroneIndex}].manufacturerCode`) ? 'invalid' : 'valid'}>
              <AuSelect
                name={`drones[${currentDroneIndex}].manufacturerCode`}
                label='Manufacturer'
                hintText='Please select your drone manufacturer'
                block
                options={[
                  {
                    text: 'Please select manufacturer',
                    value: ''
                  },
                  ...referenceData.value['DRONE_MANUFACTURER'].map(manufacturer => {
                    return {
                      text: manufacturer.casa_name,
                      value: manufacturer.casa_code
                    };
                  })
                ]}
                disabled={parent !== 'registration'} />
            </AuFormGroup>
          </div>
        </div>

        {/* Show manufacturer if Other Manufacturer */}
        {checkIfOtherManufacturer(currentDroneValues.manufacturerCode) &&
          <div className='no-gutters row'>
            <div className='float-none col-md-12 col-lg-12'>
              <AuFormGroup status={get(errors, `drones[${currentDroneIndex}].manufacturer`) ? 'invalid' : 'valid'}>
                <AuTextInput
                  name={`drones[${currentDroneIndex}].manufacturer`}
                  label='Manufacturer name'
                  hintText='Please enter the name of your drone manufacturer'
                  as='input'
                  block
                  disabled={parent !== 'registration'} />
              </AuFormGroup>
            </div>
          </div>}

        {/* Show Models if an actual Manufacturer is selected */}
        {currentDroneValues.manufacturerCode &&
          !checkIfOtherManufacturer(currentDroneValues.manufacturerCode) &&
          !checkIfHomeBuilt(currentDroneValues.manufacturerCode) &&
          <div className='no-gutters row'>
            <div className='float-none col-md-12 col-lg-12'>
              <AuFormGroup status={get(errors, `drones[${currentDroneIndex}].modelCode`) ? 'invalid' : 'valid'}>
                <AuSelect
                  name={`drones[${currentDroneIndex}].modelCode`}
                  label='Model name'
                  hintText='Please select your drone model name and version'
                  block
                  options={[
                    {
                      text: 'Please select model',
                      value: ''
                    },
                    ...referenceData.value['DRONE_MODEL']
                      .filter(model => model['parent.casa_code'] === currentDroneValues.manufacturerCode)
                      .map(model => {
                        return {
                          text: model.casa_name,
                          value: model.casa_code
                        };
                      })
                  ]}
                  disabled={parent !== 'registration' && currentDroneValues.modelUpdateRequired === false} />
              </AuFormGroup>
            </div>
          </div>}

        {/* Show model name field if manufacturer code is not yet selected or other manufacturer is selected or any other model is selected */}
        {(!currentDroneValues.manufacturerCode ||
          checkIfOtherManufacturer(currentDroneValues.manufacturerCode) ||
          checkIfOtherModel(currentDroneValues.modelCode)) &&
          <div className='no-gutters row'>
            <div className='float-none col-md-12 col-lg-12'>
              <AuFormGroup status={get(errors, `drones[${currentDroneIndex}].model`) ? 'invalid' : 'valid'}>
                <AuTextInput
                  name={`drones[${currentDroneIndex}].model`}
                  label='Model name'
                  hintText='Please enter your drone model name and version'
                  as='input'
                  block
                  disabled={parent !== 'registration'} />
              </AuFormGroup>
            </div>
          </div>}

        {/* Show Category if drone is Home-built, Other Manufacturer, or any Other Model */}
        {(checkIfOtherManufacturer(currentDroneValues?.manufacturerCode) ||
          checkIfHomeBuilt(currentDroneValues?.manufacturerCode) ||
          checkIfOtherModel(currentDroneValues.modelCode)) &&
          <div className='no-gutters row'>
            <div className='float-none col-md-12 col-lg-12'>
              <AuFormGroup status={get(errors, `drones[${currentDroneIndex}].categoryCode`) ? 'invalid' : 'valid'}>
                <AuSelect
                  name={`drones[${currentDroneIndex}].categoryCode`}
                  label='Category'
                  hintText='Please select your category/type'
                  block
                  options={[
                    {
                      text: 'Please select category',
                      value: ''
                    },
                    ...referenceData.value['DRONE_CATEGORY']
                      .map(model => {
                        return {
                          text: model.casa_name,
                          value: model.casa_code
                        };
                      })
                  ]} />
              </AuFormGroup>
            </div>
          </div>}

        {/* Show Airship callout only if Airship is selected */}
        {(checkIfOtherManufacturer(currentDroneValues.manufacturerCode) || checkIfHomeBuilt(currentDroneValues.manufacturerCode) || checkIfOtherModel(currentDroneValues.modelCode)) &&
          checkIfAirship(currentDroneValues.categoryCode) &&
          <AuFormItemWrapper>
            <AuCallout style={{ maxWidth: '34.5em' }}>
              <p>
                A remotely piloted airship with an envelope capacity of not more than 100 m<sup>3</sup> is a medium RPA.&nbsp;
                The registration fee for the airship is the same as an RPA with a gross weight of more than 25 kg, but not more than 150 kg.
              </p>
              <p>
                If your remotely piloted airship has an envelope capacity of more than 100 m<sup>3</sup>, you are required to register it as a&nbsp;<Button link className='no-padding' onClick={() => window.open('https://www.casa.gov.au/aircraft/civil-aircraft-register/aircraft-registration', '_blank')}>VH registered aircraft</Button>.
              </p>
            </AuCallout>
          </AuFormItemWrapper>}

        {/* Show WeightClass inputs if non-Airship Category is selected */}
        {((checkIfOtherManufacturer(currentDroneValues.manufacturerCode) || checkIfHomeBuilt(currentDroneValues.manufacturerCode) || checkIfOtherModel(currentDroneValues.modelCode)) &&
          currentDroneValues.categoryCode && !checkIfAirship(currentDroneValues.categoryCode)) &&
          <>
            <div className='no-gutters row'>
              <div className='float-none col-md-12 col-lg-12'>
                <AuFormGroup status={get(errors, `drones[${currentDroneIndex}].weightClassCode`) ? 'invalid' : 'valid'}>
                  <AuSelect
                    name={`drones[${currentDroneIndex}].weightClassCode`}
                    label='Weight class'
                    hintText='Please select your drone weight class. The weight of your drone includes fuel, accessories or attachments such as cameras or gimbals.'
                    block
                    options={[
                      {
                        text: 'Please select weight category',
                        value: ''
                      },
                      ...weightClassList
                        .map(model => {
                          return {
                            text: model.casa_name,
                            value: model.casa_code
                          };
                        })
                    ]} />
                </AuFormGroup>
                <AuCallout style={{ maxWidth: '34.5em' }}>
                  <p>If your drone weighs more than 150kg you are required to register it as a <Button link className='no-padding' onClick={() => window.open('https://www.casa.gov.au/aircraft/civil-aircraft-register/aircraft-registration', '_blank')}>VH registered aircraft</Button>.</p>
                </AuCallout>
              </div>
            </div>
          </>}

        <div className='no-gutters row'>
          <div className='float-none col-md-12 col-lg-12'>
            <AuFormGroup status={!get(values, `drones[${currentDroneIndex}].noSerialNumber`) && get(errors, `drones[${currentDroneIndex}].serialNumber`) ? 'invalid' : 'valid'}>
              <AuTextInput
                name={`drones[${currentDroneIndex}].serialNumber`}
                label='Serial number'
                hintText={
                  <>
                    Please enter your drone serial number. <Button link className='no-padding' onClick={() => window.open('https://www.casa.gov.au/drone-serial-numbers', '_blank')}>How do I find this?</Button>
                    {
                      currentModel && currentModel.casa_serialnumberprefix && getSerialNumberPrefixCallout()
                    }
                  </>
                }
                as='input'
                block
                disabled={get(values, `drones[${currentDroneIndex}].noSerialNumber`) ?? false} />

              {(!currentModel || currentModel && !currentModel.casa_serialnumberprefix) &&
                <AuControlInput
                  name={`drones[${currentDroneIndex}].noSerialNumber`}
                  id='checkbox-no-serial-number'
                  type='checkbox'
                  label='No serial number available'
                  block />
              }
            </AuFormGroup>
          </div>
        </div>

        {currentDroneValues.noSerialNumber &&
          <div className='no-gutters row'>
            <AuFormItemWrapper className='float-none col-md-12 col-lg-12'>
              <AuCallout>
                <p>If your drone doesn&apos;t have a serial number, you will receive a registration mark that will be recorded on the certificate of registration. The CASA-assigned registration mark must always be legibly displayed on the RPA when operating.</p>
              </AuCallout>
            </AuFormItemWrapper>
          </div>}

        {isFeatureRecDrone && parent === 'registration' &&
          <>
            <div className='no-gutters row'>
              <div className='float-none col-md-12 col-lg-12'>
                <ControlInputGroup
                  name={`drones[${currentDroneIndex}].intendedUse`}
                  label='What is the intended use of your drone?'
                  type='radio'
                  block
                  options={[
                    {
                      id: 'radio-drone-intendedUse-recreational',
                      label: 'For recreational or educational use',
                      value: 'recreational'
                    },
                    {
                      id: 'radio-drone-intendedUse-commercial',
                      label: 'For business or commercial use',
                      value: 'commercial'
                    }
                  ]} />
              </div>
            </div>
            {currentDroneValues.intendedUse === 'commercial' &&
              <AuFormItemWrapper>
                <AuCallout style={{ maxWidth: '34.5em' }}>
                  <p>Drones (<abbr title="Remotely Piloted Aircraft">RPA</abbr>) registered for business or commercial use must be registered by the owner. If the drone is owned by an organisation, the registration must be in the name of that organisation using their ARN.</p>
                </AuCallout>
              </AuFormItemWrapper>}
            {isLightWeightRecreationalDrone() &&
              <AuFormItemWrapper>
                <AuCallout style={{ maxWidth: '34.5em' }}>
                  <p><strong>This drone is less than 250g. You are not required to register any drones under 250g for recreational or educational use.</strong></p>
                </AuCallout>
              </AuFormItemWrapper>}
          </>
        }

        {parent === 'registration' &&
          <div className='no-gutters row'>
            <div className='float-none col-md-12 col-lg-12'>
              <ControlInputGroup
                name={`drones[${currentDroneIndex}].registeredOverseas`}
                label='Is this drone currently registered in another country?'
                type='radio'
                block
                options={[
                  {
                    id: 'radio-drone-registeredOverseas-yes',
                    label: 'Yes',
                    value: 'yes'
                  },
                  {
                    id: 'radio-drone-registeredOverseas-no',
                    label: 'No',
                    value: 'no'
                  }
                ]} />
            </div>
          </div>
        }

        <FormNavigation
          nextButtonText='Save drone'
          onNextAction={saveDrone}
          hidePrevious
          disableNextButton={isLightWeightRecreationalDrone()}
          saveDraftName='Drone registration'
          showSaveAndExit={parent === 'registration'} />
      </StepContainer>
    </>
  );
};

export default DroneEditStep;
